import { useCallback, useEffect, useMemo, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  MenuItem,
  Select,
  Table,
  TableBody,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers';
import { SecurityRead } from 'api';
import {
  DEFAULT_SERIES_NAME,
  EMPTY_DISPLAY_VALUE,
  EMPTY_OPTION_POOL_REFRESH,
  getNewRoundSecurityNameTakenMessage,
  NEW_ROUND_ADJUSTED_PREMONEY_FDS_LABEL,
  NEW_ROUND_ADJUSTED_PREMONEY_FDS_TOOLTIP,
  NEW_ROUND_AMOUNT_INVESTED_LABEL,
  NEW_ROUND_CALCULATIONS_HEADER,
  NEW_ROUND_CURRENT_SHARES_OUTSTANDING_LABEL,
  NEW_ROUND_CURRENT_SHARES_OUTSTANDING_TOOLTIP,
  NEW_ROUND_DILUTION_LABEL,
  NEW_ROUND_DILUTION_TOOLTIP,
  NEW_ROUND_FUND_INVESTMENT_LABEL,
  NEW_ROUND_FUND_INVESTMENT_TOOLTIP,
  NEW_ROUND_FUND_LABEL,
  NEW_ROUND_FUND_OWNERSHIP_HEADER,
  NEW_ROUND_FUND_OWNERSHIP_PCT_LABEL,
  NEW_ROUND_INCREMENTAL_OPTION_POOL_SHARES_LABEL,
  NEW_ROUND_INPUTS_HEADER,
  NEW_ROUND_INVESTMENT_DATE_LABEL,
  NEW_ROUND_INVESTMENT_DATE_TOOLTIP,
  NEW_ROUND_ISSUE_PRICE_LABEL,
  NEW_ROUND_MODELER_DESCRIPTION,
  NEW_ROUND_MODELER_TITLE,
  NEW_ROUND_NO_PREFERRED_SECURITY_ERROR,
  NEW_ROUND_NUMBER_OF_SHARES_LABEL,
  NEW_ROUND_NUMBER_OF_SHARES_TOOLTIP,
  NEW_ROUND_OPTION_POOL_REFRESH_LABEL,
  NEW_ROUND_OPTION_POOL_REFRESH_TOOLTIP,
  NEW_ROUND_PERCENT_SUFFIX,
  NEW_ROUND_POOL_REFRESH_FACTOR_LABEL,
  NEW_ROUND_POOL_REFRESH_MODE_LABEL,
  NEW_ROUND_POOL_REFRESH_MODE_TOOLTIP,
  NEW_ROUND_POST_MONEY_FDS_LABEL,
  NEW_ROUND_POST_MONEY_VALUATION_LABEL,
  NEW_ROUND_PRE_MONEY_VALUATION_LABEL,
  NEW_ROUND_PRE_MONEY_VALUATION_TOOLTIP,
  NEW_ROUND_PREMONEY_FDS_LABEL,
  NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_LABEL,
  NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_TOOLTIP,
  NEW_ROUND_SECURITY_NAME_LABEL,
  NEW_ROUND_SECURITY_NAME_TOOLTIP,
  NEW_ROUND_SHARES_ISSUED_LABEL,
  NEW_ROUND_SHARES_ISSUED_TOOLTIP,
  OPR_DECIMAL_PLACES,
  POOL_REFRESH_MODE_POST,
  POOL_REFRESH_MODE_POST_LABEL,
  POOL_REFRESH_MODE_PRE,
  POOL_REFRESH_MODE_PRE_LABEL,
  TWO_DECIMAL_PLACES,
} from 'common/constants/cap-table';
import CurrencyInputCell from './CurrencyInputCell';
import DataRow from './DataRow';
import InputCell from './InputCell';
import { NewRoundModelerProps, PoolRefreshMode } from './NewRoundModeler.props';
import { inputSlotProps, inputSx, tableStyle } from './NewRoundModeler.styles';
import {
  buildNewRoundModelerData,
  computeNewRoundModeler,
  fmtCurrency,
  fmtNumber,
  fmtPct,
  formatInvestmentDate,
  getCurrentSharesOutstanding,
  getDefaultFundId,
  getDefaultOptionPoolRefresh,
  getIssuePriceTooltip,
  getNextSeriesName,
  getPostFdsTooltip,
  getPremoneyUnallocatedOptions,
  getPrfTooltip,
  hasExistingPreferredSecurity,
  isNewRoundModelerValid,
  isSecurityNameTaken,
  parseNumber,
} from './NewRoundModeler.utils';
import SectionHeader from './SectionHeader';

const NewRoundModeler = ({ captableInfo, fundList, onChange }: NewRoundModelerProps) => {
  const [expanded, setExpanded] = useState(false);

  const securities: SecurityRead[] = useMemo(() => (captableInfo?.securities ?? []) as SecurityRead[], [captableInfo?.securities]);

  const cso = useMemo(() => getCurrentSharesOutstanding(captableInfo?.shares_diluted), [captableInfo?.shares_diluted]);

  const puo = useMemo(() => getPremoneyUnallocatedOptions(securities), [securities]);

  const defaultOPR = useMemo(() => getDefaultOptionPoolRefresh(cso, puo), [cso, puo]);
  const defaultSecurityName = useMemo(() => getNextSeriesName(securities), [securities]);
  const defaultFundId = useMemo(
    () => getDefaultFundId(captableInfo?.fund_ownership, fundList),
    [captableInfo?.fund_ownership, fundList]
  );

  const [preMoney, setPreMoney] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [optionPoolRefresh, setOptionPoolRefresh] = useState(defaultOPR);
  const [poolRefreshMode, setPoolRefreshMode] = useState<PoolRefreshMode>(POOL_REFRESH_MODE_PRE);
  const [securityName, setSecurityName] = useState(defaultSecurityName);
  const [investmentDate, setInvestmentDate] = useState<Date | null>(new Date());
  const [fundId, setFundId] = useState<number | null>(defaultFundId);
  const [fundInvestment, setFundInvestment] = useState('');
  const [fundInvestmentTouched, setFundInvestmentTouched] = useState(false);

  useEffect(() => {
    if (!optionPoolRefresh || optionPoolRefresh === EMPTY_OPTION_POOL_REFRESH) {
      setOptionPoolRefresh(defaultOPR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOPR]);

  useEffect(() => {
    setSecurityName(prev => prev || defaultSecurityName);
  }, [defaultSecurityName]);

  useEffect(() => {
    setFundId(prev => prev ?? defaultFundId);
  }, [defaultFundId]);

  // Mirror fundInvestment to amountInvested until the user manually edits it
  useEffect(() => {
    if (!fundInvestmentTouched) {
      setFundInvestment(amountInvested);
    }
  }, [amountInvested, fundInvestmentTouched]);

  const computed = useMemo(
    () =>
      computeNewRoundModeler({
        preMoney,
        amountInvested,
        optionPoolRefresh,
        poolRefreshMode,
        fundInvestment,
        cso,
        puo,
      }),
    [preMoney, amountInvested, optionPoolRefresh, poolRefreshMode, fundInvestment, cso, puo]
  );

  const hasPreferredSecurity = useMemo(() => hasExistingPreferredSecurity(securities), [securities]);

  const nameTaken = useMemo(() => isSecurityNameTaken(securities, securityName), [securities, securityName]);

  const isModelerValid = useMemo(
    () =>
      isNewRoundModelerValid({
        expanded,
        preMoney,
        amountInvested,
        fundInvestment,
        optionPoolRefresh,
        securityName,
        isNameTaken: nameTaken,
        hasPreferredSecurity,
        fundId,
        investmentDate,
      }),
    [
      expanded,
      preMoney,
      amountInvested,
      fundInvestment,
      optionPoolRefresh,
      securityName,
      nameTaken,
      hasPreferredSecurity,
      fundId,
      investmentDate,
    ]
  );

  const investmentDateStr = useMemo(() => formatInvestmentDate(investmentDate), [investmentDate]);

  const buildData = useCallback(
    () =>
      buildNewRoundModelerData({
        expanded,
        isModelerValid,
        preMoney,
        amountInvested,
        optionPoolRefresh,
        poolRefreshMode,
        securityName,
        investmentDateStr,
        fundId,
        fundInvestment,
        computed,
      }),
    [expanded, isModelerValid, preMoney, amountInvested, optionPoolRefresh, poolRefreshMode, securityName, investmentDateStr, fundId, fundInvestment, computed]
  );

  useEffect(() => {
    onChange(buildData());
  }, [buildData, onChange]);

  const issuePriceTooltip = getIssuePriceTooltip(poolRefreshMode);
  const prfTooltip = getPrfTooltip(poolRefreshMode);
  const postFdsTooltip = getPostFdsTooltip(poolRefreshMode);

  // Right-column rows differ by mode
  const calcRows = (
    <>
      <DataRow
        label={NEW_ROUND_CURRENT_SHARES_OUTSTANDING_LABEL}
        tooltip={NEW_ROUND_CURRENT_SHARES_OUTSTANDING_TOOLTIP}
        value={fmtNumber(cso)}
        color="success.dark"
      />
      <DataRow
        label={NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_LABEL}
        tooltip={NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_TOOLTIP}
        value={fmtNumber(puo)}
        color="success.dark"
      />
      <DataRow
        label={NEW_ROUND_PREMONEY_FDS_LABEL}
        value={fmtNumber(cso + puo)}
      />
      {poolRefreshMode === POOL_REFRESH_MODE_PRE && (
        <>
          <DataRow label={NEW_ROUND_POOL_REFRESH_FACTOR_LABEL} tooltip={prfTooltip} value={computed ? computed.poolRefreshFactor.toFixed(OPR_DECIMAL_PLACES) : EMPTY_DISPLAY_VALUE} />
          <DataRow label={NEW_ROUND_INCREMENTAL_OPTION_POOL_SHARES_LABEL} value={computed ? fmtNumber(Math.round(computed.incrementalOptionPoolShares)) : EMPTY_DISPLAY_VALUE} />
          <DataRow
            label={NEW_ROUND_ADJUSTED_PREMONEY_FDS_LABEL}
            tooltip={NEW_ROUND_ADJUSTED_PREMONEY_FDS_TOOLTIP}
            value={computed ? fmtNumber(Math.round(computed.adjustedPremoneyFullyDilutedShares)) : EMPTY_DISPLAY_VALUE}
          />
        </>
      )}
      <DataRow
        label={NEW_ROUND_ISSUE_PRICE_LABEL}
        tooltip={issuePriceTooltip}
        bold
        value={computed ? fmtCurrency(computed.newRoundIssuePrice, TWO_DECIMAL_PLACES) : EMPTY_DISPLAY_VALUE}
      />
      <DataRow
        label={NEW_ROUND_SHARES_ISSUED_LABEL}
        tooltip={NEW_ROUND_SHARES_ISSUED_TOOLTIP}
        bold
        value={computed ? fmtNumber(Math.round(computed.newRoundSharesIssued)) : EMPTY_DISPLAY_VALUE}
      />
      {poolRefreshMode === POOL_REFRESH_MODE_POST && (
        <>
          <DataRow label={NEW_ROUND_POOL_REFRESH_FACTOR_LABEL} tooltip={prfTooltip} value={computed ? computed.poolRefreshFactor.toFixed(OPR_DECIMAL_PLACES) : EMPTY_DISPLAY_VALUE} />
          <DataRow label={NEW_ROUND_INCREMENTAL_OPTION_POOL_SHARES_LABEL} value={computed ? fmtNumber(Math.round(computed.incrementalOptionPoolShares)) : EMPTY_DISPLAY_VALUE} />
        </>
      )}
      <DataRow
        label={NEW_ROUND_POST_MONEY_FDS_LABEL}
        tooltip={postFdsTooltip}
        value={computed ? fmtNumber(Math.round(computed.postMoneyFullyDilutedShares)) : EMPTY_DISPLAY_VALUE}
      />
      <DataRow label={NEW_ROUND_POST_MONEY_VALUATION_LABEL} value={computed ? fmtCurrency(computed.postMoneyValuation) : EMPTY_DISPLAY_VALUE} />
      <DataRow
        label={NEW_ROUND_DILUTION_LABEL}
        tooltip={NEW_ROUND_DILUTION_TOOLTIP}
        value={computed ? fmtPct(computed.dilutionToExistingHolders) : EMPTY_DISPLAY_VALUE}
      />
    </>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Accordion
        expanded={expanded}
        onChange={(_, v) => setExpanded(v)}
        disableGutters
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary
          expandIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ borderBottom: expanded ? '1px solid' : 'none', borderColor: 'divider', minHeight: 48 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5, fontSize: '0.85rem' }}>
            {NEW_ROUND_MODELER_TITLE}
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.78rem' }}>
            {NEW_ROUND_MODELER_DESCRIPTION}
          </Typography>

          {!hasPreferredSecurity && (
            <Typography variant="body2" sx={{ mb: 2, color: 'error.main', fontSize: '0.78rem' }}>
              {NEW_ROUND_NO_PREFERRED_SECURITY_ERROR}
            </Typography>
          )}
          {nameTaken && (
            <Typography variant="body2" sx={{ mb: 2, color: 'error.main', fontSize: '0.78rem' }}>
              {getNewRoundSecurityNameTakenMessage(securityName.trim())}
            </Typography>
          )}

          <Grid container spacing={2}>
            {/* Left Column */}
            <Grid size={6}>
              {/* New Round Inputs */}
              <Table size="small" sx={tableStyle}>
                <SectionHeader label={NEW_ROUND_INPUTS_HEADER} />
                <TableBody>
                  <DataRow
                    label={NEW_ROUND_PRE_MONEY_VALUATION_LABEL}
                    tooltip={NEW_ROUND_PRE_MONEY_VALUATION_TOOLTIP}
                    value={<CurrencyInputCell value={preMoney} onChange={setPreMoney} />}
                  />
                  <DataRow
                    label={NEW_ROUND_AMOUNT_INVESTED_LABEL}
                    value={<CurrencyInputCell value={amountInvested} onChange={setAmountInvested} />}
                  />
                  <DataRow
                    label={NEW_ROUND_OPTION_POOL_REFRESH_LABEL}
                    tooltip={NEW_ROUND_OPTION_POOL_REFRESH_TOOLTIP}
                    value={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <InputCell value={optionPoolRefresh} onChange={setOptionPoolRefresh} placeholder={EMPTY_OPTION_POOL_REFRESH} />
                        <Typography sx={{ fontSize: '0.8rem', color: 'primary.main', ml: 0.25 }}>{NEW_ROUND_PERCENT_SUFFIX}</Typography>
                      </Box>
                    }
                  />
                  <DataRow
                    label={NEW_ROUND_POOL_REFRESH_MODE_LABEL}
                    tooltip={NEW_ROUND_POOL_REFRESH_MODE_TOOLTIP}
                    value={
                      <Select
                        value={poolRefreshMode}
                        onChange={e => setPoolRefreshMode(e.target.value as PoolRefreshMode)}
                        variant="standard"
                        sx={{ fontSize: '0.8rem', color: 'primary.main', width: '100%', '&:before': { borderBottomColor: 'transparent' } }}
                        slotProps={{ input: { style: { textAlign: 'right' } } }}>
                        <MenuItem value={POOL_REFRESH_MODE_PRE} sx={{ fontSize: '0.8rem' }}>{POOL_REFRESH_MODE_PRE_LABEL}</MenuItem>
                        <MenuItem value={POOL_REFRESH_MODE_POST} sx={{ fontSize: '0.8rem' }}>{POOL_REFRESH_MODE_POST_LABEL}</MenuItem>
                      </Select>
                    }
                  />
                  <DataRow
                    label={NEW_ROUND_SECURITY_NAME_LABEL}
                    tooltip={NEW_ROUND_SECURITY_NAME_TOOLTIP}
                    value={<InputCell value={securityName} onChange={setSecurityName} placeholder={DEFAULT_SERIES_NAME} />}
                  />
                  <DataRow
                    label={NEW_ROUND_INVESTMENT_DATE_LABEL}
                    tooltip={NEW_ROUND_INVESTMENT_DATE_TOOLTIP}
                    value={
                      <DatePicker
                        value={investmentDate}
                        onChange={setInvestmentDate}
                        slotProps={{
                          textField: {
                            variant: 'standard',
                            slotProps: { htmlInput: inputSlotProps.htmlInput },
                            sx: inputSx,
                          },
                        }}
                      />
                    }
                  />
                </TableBody>
              </Table>

              {/* New Round Fund Ownership */}
              <Table size="small" sx={[tableStyle, { mt: 1 }]}>
                <SectionHeader label={NEW_ROUND_FUND_OWNERSHIP_HEADER} />
                <TableBody>
                  <DataRow
                    label={NEW_ROUND_FUND_LABEL}
                    value={
                      <Select
                        value={fundId ?? ''}
                        onChange={e => setFundId(e.target.value as number)}
                        variant="standard"
                        sx={{ fontSize: '0.8rem', color: 'primary.main', width: '100%', '&:before': { borderBottomColor: 'transparent' } }}
                        slotProps={{ input: { style: { textAlign: 'right' } } }}>
                        {(fundList ?? []).map(f => (
                          <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.8rem' }}>
                            {f.name}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                  />
                  <DataRow
                    label={NEW_ROUND_FUND_INVESTMENT_LABEL}
                    tooltip={NEW_ROUND_FUND_INVESTMENT_TOOLTIP}
                    value={
                      <CurrencyInputCell
                        value={fundInvestment}
                        onChange={v => {
                          setFundInvestmentTouched(true);
                          setFundInvestment(v);
                        }}
                      />
                    }
                  />
                  <DataRow
                    label={NEW_ROUND_FUND_OWNERSHIP_PCT_LABEL}
                    value={computed && parseNumber(fundInvestment) > 0 ? fmtPct(computed.fundOwnershipPct) : EMPTY_DISPLAY_VALUE}
                  />
                  <DataRow
                    label={NEW_ROUND_NUMBER_OF_SHARES_LABEL}
                    tooltip={NEW_ROUND_NUMBER_OF_SHARES_TOOLTIP}
                    bold
                    value={computed && parseNumber(fundInvestment) > 0 ? fmtNumber(Math.round(computed.numberOfShares)) : EMPTY_DISPLAY_VALUE}
                  />
                </TableBody>
              </Table>
            </Grid>

            {/* Right Column */}
            <Grid size={6}>
              <Table size="small" sx={tableStyle}>
                <SectionHeader label={NEW_ROUND_CALCULATIONS_HEADER} />
                <TableBody>
                  {calcRows}
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default NewRoundModeler;
